import React, {
  createContext,
  useContext,
  useCallback,
  useState,
  useEffect,
  ChangeEvent,
  ReactNode,
} from 'react';
import { IconBaseProps } from 'react-icons/lib';
import { useDispatch, useSelector } from 'react-redux';
import { StoreState } from '../../../store/createStore';
import { customerUpdateRequest } from '../../../store/modules/auth/actions';
import {
  findMethods,
  methodFavorite,
  methodRemove,
  setMessageToast,
} from '../../../store/modules/parameter/actions';

import * as Yup from 'yup';
import { bValidCpf } from '../../../utils/bValidCpf';
import { bValidBirthDay } from '../../../utils/bValidBirthDay';
import getValidationErrors from '../../../utils/getValidationErrors';
import { routesEndpoints } from '../../../routes/endpoint';

interface MenuProps {
  id: number;
  active: boolean;
  name: string;
  title: string;
  icon?: IconBaseProps;
}

interface CustomerProfile {
  email?: string;
  first_name?: string;
  last_name?: string;
  new_password?: string;
  new_password_confirmation?: string;
  gender?: string;
  date_of_birth?: string;
  customer_id?: string;
  customer_id_alt?: string;
  phone?: string;
}

interface HooksAccountData {
  saveProfile(customer: CustomerProfile): void;
  handledChange(event: ChangeEvent): void;
  handledMethodFavorite(method_id: number | undefined): void;
  handledMethodRemove(method_id: number | undefined): void;
  setOpenViewPassword(openViewPassword: boolean): void;
  onSetOpenViewCreatePayment(): void;
  openViewPassword: boolean;
  openViewCreatePayment: boolean;
  customerData: CustomerProfile;
}

const AccountContext = createContext<HooksAccountData>({} as HooksAccountData);

export const menuAccount = [
  {
    id: 1,
    path: routesEndpoints.NEW_ACCOUNT,
    name: 'main.new_account.account_data.my_data',
    title: 'Meus dados',
  },
  {
    id: 2,
    path: routesEndpoints.NEW_ACCOUNT_PAYMENT,
    name: 'main.new_account.account_data.my_cards',
    title: 'Meus cartões e formas de pagamentos',
  },
  {
    id: 3,
    path: routesEndpoints.NEW_ACCOUNT_ADDRESS,
    name: 'main.new_account.account_data.my_addresses',
    title: 'Meus endereços',
  },
  {
    id: 4,
    path: routesEndpoints.NEW_ACCOUNT_ORDERS,
    name: 'main.new_account.account_data.my_orders',
    title: 'Meus pedidos',
  },
  {
    id: 5,
    path: routesEndpoints.NEW_ACCOUNT_CANCEL,
    name: 'main.new_account.account_data.cancel_account',
    title: 'Cancelar conta',
  },
];

interface AccountContextProviderProps {
  children: ReactNode;
}

const AccountContextProvider: React.FC<AccountContextProviderProps> = ({
  children,
}: AccountContextProviderProps) => {

  const [customerData, setCustomerData] = useState<CustomerProfile>({
    first_name: '',
    last_name: '',
    email: '',
    date_of_birth: '',
    customer_id: '',
    phone: '',
  });
  const [openViewPassword, setOpenViewPassword] = useState(false); // default: false
  const [openViewCreatePayment, setOpenViewCreatePayment] = useState(true); // default: false

  const onSetOpenViewCreatePayment = useCallback(() => {
    setOpenViewCreatePayment(v => !v);
  }, []);

  const dispatch = useDispatch();

  const {
    user: { data: userData },
  } = useSelector((state: StoreState) => state.auth);

  const handledChange = useCallback(
    event => {
      const { name, value } = event.target;
      setCustomerData(v => ({ ...v, [name]: value }));
    },
    [setCustomerData],
  );

  // useEffect(() => {
  //   if (openMenuAccuntId) {
  //     dispatch(openMenuAccountClear());
  //     handledSideMenuSel(sideMenu[openMenuAccuntId]);
  //   }
  // }, [openMenuAccuntId, sideMenu, handledSideMenuSel, dispatch]);

  const saveProfile = useCallback(
    async (customerDataProfile: CustomerProfile) => {
      try {
        const schema = Yup.object().shape({
          first_name: Yup.string().required('Nome obrigatório'),
          last_name: Yup.string().required('Sobrenome obrigatório'),
          customer_id: Yup.string()
            .transform((value: string) => {
              return value.split(/\D/).join('');
            })
            .test({
              test: customer_id => bValidCpf(customer_id),
              message: 'CPF inválido',
            })
            .required('CPF obrigatório'),
          date_of_birth: Yup.string()
            .transform((value: string) => {
              return value.split(/\D/).join('');
            })
            .test({
              test: birth => bValidBirthDay(birth),
              message: 'Data de nascimento inválida',
            })
            .min(6, 'Minímo 6 dígitos')
            .max(8, 'Máximo 8 dígitos')
            .required('Data de nascimento obrigatório'),
          phone: Yup.string()
            .matches(/^\d{2}\d{4,5}\d{4}$/gi, 'Celular invalido (DD NNNN-NNNN)')
            .required('Celular obrigatório')
            .test({
              test: phone_mobile => !isNaN(phone_mobile),
              message: 'Digite apenas números no celular',
            }),
          email: Yup.string()
            .required('E-mail obrigatório')
            .email('Digite um e-mail válido'),
        });

        await schema.validate(customerDataProfile, {
          abortEarly: false,
        });
        if (
          customerDataProfile.date_of_birth &&
          customerDataProfile.date_of_birth.length > 0
        ) {
          const date_of_birth = customerDataProfile.date_of_birth
            .split('/')
            .reverse()
            .join('-');
          customerDataProfile.date_of_birth = date_of_birth;
        }

        if (
          customerDataProfile.customer_id &&
          customerDataProfile.customer_id.length > 0
        ) {
          const customer_id = customerDataProfile.customer_id
            .split('.')
            .join('')
            .replace(/\s_/g, '');
          customerDataProfile.customer_id = customer_id;
        }

        const customerProfile = {} as CustomerProfile;
        if (customerDataProfile.email)
          customerProfile.email = customerDataProfile.email;
        if (customerDataProfile.first_name)
          customerProfile.first_name = customerDataProfile.first_name;
        if (customerDataProfile.last_name)
          customerProfile.last_name = customerDataProfile.last_name;
        if (customerDataProfile.new_password)
          customerProfile.new_password = customerDataProfile.new_password;
        if (customerDataProfile.new_password_confirmation)
          customerProfile.new_password_confirmation =
            customerDataProfile.new_password_confirmation;
        if (customerDataProfile.gender)
          customerProfile.gender = customerDataProfile.gender;
        if (customerDataProfile.date_of_birth)
          customerProfile.date_of_birth = customerDataProfile.date_of_birth;
        if (customerDataProfile.customer_id)
          customerProfile.customer_id = customerDataProfile.customer_id;
        if (customerDataProfile.phone)
          customerProfile.phone = customerDataProfile.phone;

        dispatch(customerUpdateRequest(customerProfile));
      } catch (error) {
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);
          for (const message in errors) {
            await dispatch(setMessageToast({
              type:'error', message: errors[message]
            }));
          }
        }
      }
    },
    [dispatch],
  );

  const handledMethodFavorite = useCallback(
    (method_id: number) => {
      //konsole.log('handledMethodFavorite(): ', method_id);
      if (method_id) {
        dispatch(methodFavorite(method_id));
      }
    },
    [dispatch],
  );

  const handledMethodRemove = useCallback(
    (method_id: number) => {
      //konsole.log('handledMethodRemove(): ', method_id);
      dispatch(methodRemove(method_id));
      setTimeout(() => {
        dispatch(findMethods());
      }, 300);
    },
    [dispatch],
  );

  useEffect(() => {
    if (userData.date_of_birth) {
      const date_of_birth_01 = userData.date_of_birth
        .split('-')
        .reverse()
        .join('/');
      userData.date_of_birth = date_of_birth_01;
    }
    setCustomerData(userData);
  }, [userData]);

  return (
    <AccountContext.Provider
      value={{
        saveProfile,
        handledChange,
        handledMethodFavorite,
        handledMethodRemove,
        setOpenViewPassword,
        onSetOpenViewCreatePayment,
        openViewPassword,
        openViewCreatePayment,
        customerData,
      }}
    >
      {children}
    </AccountContext.Provider>
  );
};

function useAccount(): HooksAccountData {
  const context = useContext(AccountContext);

  if (!context) {
    throw new Error('useAccount must be used within a useAccount');
  }

  return context;
}

export { AccountContextProvider, useAccount };
